import type { PageVisibilityService } from '.';
import { batch, signal } from '@preact/signals-core';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import { injectable, skipAutoDispose } from 'src/features/ioc';

@injectable()
export class PageVisibilityServiceImpl implements PageVisibilityService {
    readonly [skipAutoDispose] = true;
    readonly #isHidden = signal(document.visibilityState === 'hidden');
    readonly #isIdle = signal(this.#isHidden.value);
    readonly #idleTimer = new DisposableBox();

    readonly isHidden: PageVisibilityService['isHidden'] = this.#isHidden;
    readonly isIdle: PageVisibilityService['isIdle'] = this.#isIdle;

    constructor() {
        document.addEventListener('visibilitychange', this.#onVisibilityChange);
    }

    #onVisibilityChange = (): void => {
        const isHidden = document.visibilityState === 'hidden';
        if (isHidden === this.#isHidden.peek()) return;

        this.#idleTimer.clear();
        if (isHidden) {
            // Brief switches away from the page should not slow table updates.
            const timer = setTimeout(() => {
                this.#idleTimer.clear();
                this.#isIdle.value = document.visibilityState === 'hidden';
            }, 1000);
            this.#idleTimer.next({ [Symbol.dispose]: () => clearTimeout(timer) });
        }

        batch(() => {
            this.#isHidden.value = isHidden;
            if (!isHidden) this.#isIdle.value = false;
        });
    };

    [Symbol.dispose](): void {
        document.removeEventListener('visibilitychange', this.#onVisibilityChange);
        this.#idleTimer[Symbol.dispose]();
    }
}
